import { useTranslate } from '@repo/shared/hooks'
import { Button, Card, Col, Row, Typography, toast } from '@repo/ui/components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CancelButton } from './selfie-kyc.cancel-button'

export const RECORDING_DURATION = 30

// Video compression settings - using 9:16 aspect ratio for selfie
const COMPRESSION_SETTINGS = {
  width: 480,
  height: 853, // 9:16 aspect ratio (480 * 16/9 = 853.33...)
  fps: 30,
  videoBitsPerSecond: 500_000, // 500 kbps for smaller file size
  mimeType: 'video/webm; codecs=vp8' as const,
}

export const SelfieKycRecording = ({
  onStop,
  onTimeout,
  onRecorded,
  dynamicTextProps = {},
}: {
  onStop: () => void
  onTimeout: () => void
  onRecorded: (blob: Blob) => void
  dynamicTextProps?: Record<string, unknown>
}) => {
  const { tRich, t } = useTranslate()
  const [seconds, setSeconds] = useState(RECORDING_DURATION)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunks = useRef<Blob[]>([])
  const animationFrameRef = useRef<number | null>(null)

  // Stop all media tracks (video and audio)
  const stopMediaStream = useCallback(() => {
    // Stop all tracks in the media stream
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => {
        track.stop()
        track.enabled = false
      })
      setMediaStream(null)
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current.pause()
    }

    // Stop media recorder if active
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop()
    }

    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [mediaStream])

  // Initialize canvas for compression
  const initializeCanvas = () => {
    if (!canvasRef.current) return

    canvasRef.current.width = COMPRESSION_SETTINGS.width
    canvasRef.current.height = COMPRESSION_SETTINGS.height
  }

  // Draw video frames to canvas for compression with aspect ratio preservation
  const drawVideoToCanvas = () => {
    if (!videoRef.current || !canvasRef.current || !mediaRecorderRef.current)
      return

    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return

    const video = videoRef.current
    const canvas = canvasRef.current

    // Get video dimensions
    const videoWidth = video.videoWidth
    const videoHeight = video.videoHeight

    // Calculate target dimensions maintaining 9:16 aspect ratio
    const targetAspectRatio = 9 / 16
    const videoAspectRatio = videoWidth / videoHeight

    let sourceX = 0
    let sourceY = 0
    let sourceWidth = videoWidth
    let sourceHeight = videoHeight

    // If video is wider than 9:16, crop from center
    if (videoAspectRatio > targetAspectRatio) {
      sourceWidth = videoHeight * targetAspectRatio
      sourceX = (videoWidth - sourceWidth) / 2
    }
    // If video is taller than 9:16, crop from center
    else if (videoAspectRatio < targetAspectRatio) {
      sourceHeight = videoWidth / targetAspectRatio
      sourceY = (videoHeight - sourceHeight) / 2
    }

    // Clear canvas
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw video maintaining aspect ratio
    ctx.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight, // Source rectangle
      0,
      0,
      canvas.width,
      canvas.height, // Destination rectangle
    )

    // Continue drawing frames if recording is active
    if (mediaRecorderRef.current.state === 'recording') {
      animationFrameRef.current = requestAnimationFrame(drawVideoToCanvas)
    }
  }

  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error({
        title: t('selfie_kyc_error_not_supported'),
      })
      onStop()
      return
    }

    // Initialize canvas
    initializeCanvas()

    const requestMediaAccess = async (retryCount = 0) => {
      const MAX_RETRIES = 2

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        })

        setMediaStream(stream)
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }

        // Setup canvas stream for compression
        if (!canvasRef.current) return

        const canvasStream = canvasRef.current.captureStream(
          COMPRESSION_SETTINGS.fps,
        )

        // Combine video from canvas with audio from original stream
        const audioTrack = stream.getAudioTracks()[0]
        if (audioTrack) {
          canvasStream.addTrack(audioTrack)
        }

        // MediaRecorder setup with compression settings
        const recorder = new MediaRecorder(canvasStream, {
          mimeType: COMPRESSION_SETTINGS.mimeType,
          videoBitsPerSecond: COMPRESSION_SETTINGS.videoBitsPerSecond,
        })

        mediaRecorderRef.current = recorder
        recordedChunks.current = []

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) recordedChunks.current.push(e.data)
        }

        recorder.onstop = () => {
          const blob = new Blob(recordedChunks.current, { type: 'video/webm' })
          stopMediaStream()
          onRecorded(blob)
        }

        recorder.start()

        // Start drawing frames to canvas
        drawVideoToCanvas()
      } catch (err: any) {
        // Determine error message based on error type
        let errorMessage = t('selfie_kyc_error_permission_denied')

        if (
          err?.name === 'NotAllowedError' ||
          err?.name === 'PermissionDeniedError'
        ) {
          errorMessage = t('selfie_kyc_error_permission_denied')
          // If permission was denied, don't retry - user needs to grant permission manually
          toast.error({
            title: errorMessage,
          })
          onStop()
          return
        } else if (
          err?.name === 'NotFoundError' ||
          err?.name === 'DevicesNotFoundError'
        ) {
          errorMessage = t('selfie_kyc_error_device_not_found')
        } else if (
          err?.name === 'NotReadableError' ||
          err?.name === 'TrackStartError'
        ) {
          errorMessage = t('selfie_kyc_error_device_in_use')
          // Device might be busy, retry after a short delay
          if (retryCount < MAX_RETRIES) {
            setTimeout(
              () => {
                requestMediaAccess(retryCount + 1)
              },
              1000 * (retryCount + 1),
            ) // Exponential backoff
            return
          }
        } else if (
          err?.name === 'OverconstrainedError' ||
          err?.name === 'ConstraintNotSatisfiedError'
        ) {
          errorMessage = t('selfie_kyc_error_constraints_not_satisfied')
        } else if (err?.name === 'AbortError') {
          // Request was aborted, might be temporary, retry once
          if (retryCount < 1) {
            setTimeout(() => {
              requestMediaAccess(retryCount + 1)
            }, 500)
            return
          }
        }

        // Show error and stop if max retries reached or non-retryable error
        toast.error({
          title: errorMessage,
        })
        onStop()
      }
    }

    requestMediaAccess()

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      stopMediaStream()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream
    }
  }, [mediaStream])

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current!)
          stopMediaStream()
          onTimeout()
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current!)
  }, [onTimeout, stopMediaStream])

  const handleStop = () => {
    stopMediaStream()
    onStop()
  }

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  return (
    <Card
      className='!p-0 overflow-hidden relative h-[100dvh]  rounded-none border-none'
      style={{ maxHeight: '100dvh', minHeight: '100dvh', height: '100dvh' }}
    >
      <CancelButton />
      <div className='w-full flex justify-center items-center overflow-hidden h-full'>
        <div className='relative w-full h-full'>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className='bg-black w-full aspect-[9/16] object-cover h-full'
            style={{
              maxHeight: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          <div className='absolute inset-0 bg-black/50 mask-ellipse' />
        </div>
      </div>

      {/* Hidden canvas for video compression */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <Col className='gap-md items-center p-md'>
        <Typography variant='p' size='md'>
          {tRich.rich('selfie_kyc_recording_text', {
            ...dynamicTextProps,
            securityCode: (chunks) => chunks,
            b: (chunks) => <b>{chunks}</b>,
          })}
        </Typography>
        <Row className='justify-between'>
          <Row className='gap-xs w-full items-center'>
            <div className='bg-negative-surface-100 rounded-full w-xs h-xs' />
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('selfie_kyc_recording_status')}
            </Typography>
            <Typography size='xs' color='text-neutral-surface-60'>
              {formatTime(seconds)}
            </Typography>
          </Row>
          <Button
            variant='ghost'
            color='neutral'
            size='md'
            width='auto'
            className='min-w-[160px]'
            onClick={handleStop}
          >
            {t('selfie_kyc_recording_stop')}
          </Button>
        </Row>
      </Col>
    </Card>
  )
}
